import { useCallback, useEffect, useMemo, useState } from 'react';
import { IBaseSubscriptions, ISalesRepCommissions, ISubscriptions } from '@llws/lift-entity-interfaces';
import { getMany, getOne } from 'src/redux/features/dynamic/actions';
import { Equal } from '@llws/dynamic-router-utils';
import { first, Nullable } from '@jamesgmarks/utilities';
import { formatDecimalToPercentage, roundFloatToCents, sortBy, appendRange } from '@hydra/internal';
import { ICommissionSchemeData } from '../EditorComponents/CommissionEditor/CommissionEditor';
import { IVBPData } from '../EditorComponents/VariableBasePriceEditor/VariableBasePriceEditor';
import { CustomConfigSchema } from './CustomConfigEditor/customConfigMeta';
import { ISubscriptionData, TJsonNoteWithFlag } from './types';
import { createNote, getEmptySubscriptionData, getRevenueTrackingCodeFromSubscription } from './utils';
import { useDispatch } from 'react-redux';

/**
 * Custom hook for managing subscription data and related API calls
 */
export const useSubscriptionData = (subscriptionId: Nullable<number>) => {
  const fetchCommissionSchemes = useCallback(
    async (subscriptionId: number) => {
      getMany(
        'commission_schemes',
        { where: [Equal('subscriptionId', subscriptionId)] },
      );
    }, [],
  );

  const fetchSalesReps = useCallback(async () => {
    getMany('sales_reps', {
      alias: 'sr',
      relations: [{ path: 'sr.user', alias: 'u' }],
      where: [Equal('status', 'active')],
    });
  }, []);

  const fetchBillingFrequencies = useCallback(async () => {
    getMany('billing_frequencies', {
      alias: 'bf',
    });
  }, []);

  const fetchSubscription = useCallback(
    async (subscriptionId: number) => {
      getOne('subscriptions', subscriptionId, [
        { path: 'client', alias: 'c' },
        { path: 'variableBasePriceOverrides', alias: 'vbpo' },
        { path: 'billingZoneCityOverrides', alias: 'bzco' },
        { path: 'bzco.ownershipGroup', alias: 'bzcoog' },
        { path: 'bzco.city', alias: 'bzcogc' },
        { path: 'bzcogc.province', alias: 'bzcogp' },
        { path: 'baseSubscription', alias: 'bs' },
        { path: 'bs.variableBasePrices', alias: 'vbp' },
        { path: 'bs.billingZones', alias: 'bz' },
        { path: 'bz.billingZoneCities', alias: 'bzc' },
        { path: 'bzc.city', alias: 'gc' },
        { path: 'gc.province', alias: 'gp' },
        { path: 'bs.service', alias: 'svc' },
        { path: 'bs.partner', alias: 'p' },
        { path: 'liftSubscriptions', alias: 'ls' },
        { path: 'subscriptionsHasBuildings', alias: 'shb' },
        { path: 'shb.building', alias: 'shbb' },
        { path: 'parentSubscription', alias: 'ps' },
        { path: 'workInProgressQueues', alias: 'wipqs' },
        { path: 'wipqs.workInProgress', alias: 'wip' },
        { path: 'salesRep', alias: 'sr' },
        { path: 'parentSalesRep', alias: 'psr' },
        { path: 'sr.user', alias: 'u' },
        { path: 'psr.user', alias: 'pu' },
        { path: 'commissionSchemes', alias: 'cs' },
      ]);
    }, [],
  );

  useEffect(() => {
    if (subscriptionId) {
      fetchSubscription(subscriptionId);
    }
    fetchSalesReps();
    fetchBillingFrequencies();
  }, [subscriptionId, fetchSubscription, fetchSalesReps, fetchBillingFrequencies]);

  return {
    fetchCommissionSchemes,
    fetchSubscription,
    fetchSalesReps,
    fetchBillingFrequencies,
  };
};

/**
 * Custom hook for managing commission & commission scheme data derived from store subscription
 */
export const useCommissionScheme = (
  storeSubscription: Nullable<ISubscriptions>,
  subscriptionId: Nullable<number>,
) => {
  const commissionSchemeDataFromStoreSubscription = useMemo(
    (): ICommissionSchemeData => {
      const salesRepId = (
        storeSubscription?.salesRepId
        ?? storeSubscription?.client?.salesRepId
        ?? null
      );

      const commissionScheme = first(storeSubscription?.commissionSchemes ?? []) ?? null;

      return {
        id: commissionScheme?.id ?? null,
        subscriptionId,
        salesRepId,
        scheme: commissionScheme?.scheme ?? null,
        label: commissionScheme?.label ?? '',
      };
    },
    [storeSubscription, subscriptionId],
  );

  const [commissionSchemeData, setCommissionSchemeData] = (
    useState<ICommissionSchemeData>(commissionSchemeDataFromStoreSubscription));

  useEffect(() => {
    setCommissionSchemeData(commissionSchemeDataFromStoreSubscription);
  }, [commissionSchemeDataFromStoreSubscription]);

  return {
    commissionSchemeData,
    setCommissionSchemeData,
  };
};

/**
 * Custom hook to fetch the default commission rate for a sales rep and base subscription
 * Usage: const commissionRate = useDefaultCommissionRate(salesRepId, baseSubscriptionId);
 */
export const useDefaultCommissionForSalesRep = (salesRepId: Nullable<number>, baseSubscriptionId: Nullable<number>) => {
  const fetchDefaultCommissionForSalesRep = useCallback(
    async () => {
      getMany('sales_rep_commissions', {
        where: [Equal('salesRepId', salesRepId), Equal('baseSubscriptionId', baseSubscriptionId)],
      });
    },
    [salesRepId, baseSubscriptionId]
  );
  useEffect(() => {
    if (salesRepId && baseSubscriptionId) {
      fetchDefaultCommissionForSalesRep();
    }
  }, [salesRepId, baseSubscriptionId, fetchDefaultCommissionForSalesRep]);

  return {fetchDefaultCommissionForSalesRep};
};

/**
 * Custom hook for managing variable base price data derived from store subscription
 */
export const useVBPData = (
  storeSubscription: Nullable<ISubscriptions>, baseSubscription: Nullable<IBaseSubscriptions>,
) => {
  const variablePriceDataFromStoreSubscription = useMemo(
    () => {
      const sortedVariableBasePrices = sortBy(
        baseSubscription?.variableBasePrices ?? [], 'maxValue',
      );
      const sortedVariableBasePriceOverrides = sortBy(storeSubscription?.variableBasePriceOverrides ?? [], 'maxValue');

      const rangedVBP = appendRange(sortedVariableBasePrices, 'maxValue');
      const rangedVBPO = appendRange(sortedVariableBasePriceOverrides, 'maxValue');
      return {
        rangedVBP,
        rangedVBPO,
      };
    }, [storeSubscription, baseSubscription],
  );

  const defaultVBPData = useMemo(
    () => variablePriceDataFromStoreSubscription, [variablePriceDataFromStoreSubscription],
  );

  const [vBPData, setVBPData] = (
    useState<IVBPData>(variablePriceDataFromStoreSubscription)
  );

  useEffect(() => {
    setVBPData(variablePriceDataFromStoreSubscription);
  }, [variablePriceDataFromStoreSubscription]);

  return {
    vBPData,
    defaultVBPData,
    setVBPData,
  };
};

/**
 * Custom hook for managing subscription form data derived from store subscription
 */
export const useSubscriptionFormData = (
  storeSubscription: Nullable<ISubscriptions>,
) => {
  const [discountRateFormValue, setDiscountRateFormValue] = useState('');
  const [commissionRateFormValue, setCommissionRateFormValue] = useState('');

  const subscriptionDataFromStoreSubscription = useMemo(
    (): ISubscriptionData => {
      if (storeSubscription) {
        const discountRate = formatDecimalToPercentage(storeSubscription.discountRate);
        const commissionRate = formatDecimalToPercentage(storeSubscription.commissionRate);

        setDiscountRateFormValue(`${discountRate}`);
        setCommissionRateFormValue(`${commissionRate}`);

        const customConfigurationParsed = CustomConfigSchema.safeParse(storeSubscription.customConfiguration);

        return {
          baseSubscriptionId: storeSubscription.baseSubscriptionId,
          clientId: storeSubscription.clientId,
          clientFreshbooksId: storeSubscription.client?.freshbooksClientId ?? null,
          startDate: storeSubscription.startDate ?? null,
          expiryDate: storeSubscription.expiryDate ?? null,
          rateExpiryDate: storeSubscription.rateExpiryDate ?? null,
          commissionRate: commissionRate,
          salesRepId: storeSubscription.salesRepId ?? null,
          minimumPropertiesForRate: storeSubscription.minPropertiesForRate ?? null,
          maxPropertiesForRate: storeSubscription.maxPropertiesForRate ?? null,
          poNumber: storeSubscription.poNumber ?? null,
          discountRate,
          basePrice: storeSubscription.basePrice ?? null,
          basePriceScalar: storeSubscription.basePriceScalar ?? null,
          costPerLeadMax: storeSubscription.costPerLeadMax ?? null,
          quoteId: storeSubscription.quoteId ?? '',
          revenueTrackingCode: getRevenueTrackingCodeFromSubscription(storeSubscription),
          invoiceDescription: storeSubscription.invoiceDescription,
          notes: storeSubscription.notes ?? null,
          buildingsData: (
            (storeSubscription?.subscriptionsHasBuildings ?? []).map((shb) => ({
              id: shb.buildingId,
              name: shb.building?.buildingName ?? '',
              price: shb.price ? `${roundFloatToCents(shb.price)}` : '0',
            }))
          ),
          customConfiguration: customConfigurationParsed.success ? customConfigurationParsed.data : null,
          billingTypeId: storeSubscription.baseSubscription?.service?.billingTypeId ?? 0,
          evenBuildingSplit: storeSubscription.evenBuildingSplit ?? 0,
          billingFrequencyId: storeSubscription.billingFrequencyId ?? 3,
          liftSubscriptions: {
            minimumValue: storeSubscription.liftSubscriptions?.minimumValue ?? 0,
            costPerSingleFamilyHome: storeSubscription.liftSubscriptions?.costPerSingleFamilyHome ?? 0,
            costPerMultiFamilyHome: storeSubscription.liftSubscriptions?.costPerMultiFamilyHome ?? 0,
          },
        };
      }

      return getEmptySubscriptionData();
    }, [storeSubscription],
  );

  const [subscriptionData, setSubscriptionData] = (
    useState<ISubscriptionData>(subscriptionDataFromStoreSubscription));

  const [selectedBaseSubscription, setSelectedBaseSubscription] = (
    useState<Nullable<IBaseSubscriptions>>(storeSubscription?.baseSubscription ?? null));

  useEffect(() => {
    setSubscriptionData(subscriptionDataFromStoreSubscription);
  }, [subscriptionDataFromStoreSubscription]);

  useEffect(() => {
    if (storeSubscription?.baseSubscription) {
      setSelectedBaseSubscription(storeSubscription.baseSubscription);
    }
  }, [storeSubscription?.baseSubscription]);

  useEffect(() => {
    setDiscountRateFormValue(`${subscriptionData.discountRate}`);
  }, [subscriptionData.discountRate]);

  useEffect(() => {
    setCommissionRateFormValue(`${subscriptionData.commissionRate}`);
  }, [subscriptionData.commissionRate]);

  return {
    subscriptionData,
    setSubscriptionData,
    discountRateFormValue,
    setDiscountRateFormValue,
    commissionRateFormValue,
    setCommissionRateFormValue,
    selectedBaseSubscription,
    setSelectedBaseSubscription,
  };
};

/**
 * Custom hook for managing notes functionality
 */
export const useNotesManager = (
  subscriptionData: ISubscriptionData,
  userId: Nullable<number>,
  userName: Nullable<string>,
) => {
  const [newNotes, setNewNotes] = useState<TJsonNoteWithFlag[]>([]);

  const subscriptionNotes = useMemo(
    () => {
      if (subscriptionData.notes) {
        const sortedNotes = [...subscriptionData.notes].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
        return [...newNotes, ...sortedNotes];
      }
      return newNotes;
    },
    [subscriptionData.notes, newNotes],
  );

  const onDeleteNote = useCallback(
    (index: number) => {
      setNewNotes((prevNotes) => {
        const updatedNotes = [...prevNotes];
        if (updatedNotes[index].localOnly) {
          updatedNotes.splice(index, 1);
        }
        return updatedNotes;
      });
    }, [],
  );

  const onAddNote = useCallback(
    (noteText: string) => {
      if (!userId) {
        throw new Error('Valid userId is required to add a note');
      }
      if (!userName) {
        throw new Error('Valid userName is required to add a note');
      }
      const newNote = createNote(noteText, userId, userName);
      setNewNotes((prevNotes) => [newNote, ...prevNotes]);
    }, [userId, userName],
  );

  return {
    subscriptionNotes,
    newNotes,
    onDeleteNote,
    onAddNote,
  };
};
